import type { RollSubmitOutcome } from "@/components/attendance/RollRegister";

export const PUBLICATION_WAITS = "Publication waits for a saved plan with a session objective and any conflict resolved.";
export const KEEP_MARKS_EFFECT = "Records your changed marks; keeps theirs for every other learner.";

/** The next act and its effects share these decisions, including conflict choices. */
export interface CloseAct {
  label: string;
  roll: "submit" | "adopt" | "keep" | "load" | "choose" | "none";
  publication: "publish" | "check" | "none";
  update: boolean;
  holds: boolean;
  review: boolean;
  keepsUnmarked: boolean;
  publicationWaiting: boolean;
}

export interface ClosePlanState {
  held: boolean;
  changedMarks: boolean;
  marked: boolean;
  statusPending: boolean;
  canRecord: boolean;
  publish: boolean;
  planReady: boolean;
  prepareChanged: boolean;
  planChanged: boolean;
  revealOnHold: boolean;
  publicationFailed: boolean;
  checkPending: boolean;
  recovery: { update: boolean; publish: boolean; loaded: boolean; wasHeld?: boolean } | null;
  /** A review choice uses the same planner as the footer it confirms. */
  confirmation?: "publish" | "private";
}

export interface ClosePlan {
  footer?: CloseAct;
  choices?: { mine?: CloseAct; theirs: CloseAct };
  effectsLabel?: string;
  lines: string[];
}

/** Recording and holding are separate facts, including older saved partial rolls. */
export function rollRecordingState(held: boolean, changed: boolean, recorded: number, total: number): string {
  if (held) return changed ? "Roll changed, not recorded" : "Roll recorded";
  if (recorded > 0) return `${recorded} of ${total} recorded${changed ? "; changes not recorded" : ""}`;
  return "Roll not recorded";
}

export function planClose(state: ClosePlanState): ClosePlan {
  const act = (overrides: Partial<CloseAct>): CloseAct => {
    const next: CloseAct = { label: "Close session", roll: "none", publication: "none", update: false, holds: false, review: false, keepsUnmarked: false, publicationWaiting: false, ...overrides };
    if (next.publication === "publish" && !state.planReady) {
      next.publication = "none";
      next.publicationWaiting = true;
      next.review = false;
    }
    return next;
  };
  const effects = (next: CloseAct): string[] => {
    if (next.publication === "check") return ["Checks the last request. Sends no marks or publication."];
    if (next.roll === "choose") return ["Choose a version of the roll.", ...(next.update ? ["Publishes nothing."] : [])];
    if (next.roll === "load") return ["Loads their recorded roll without sending your marks.", "Publishes nothing."];
    const lines: string[] = [];
    if (next.roll === "adopt") lines.push("Keeps their recorded roll.");
    if (next.roll === "keep" || next.keepsUnmarked) lines.push(KEEP_MARKS_EFFECT);
    else if (next.roll === "submit") lines.push(next.update ? "Records the changed marks." : "Records the roll.");
    if (next.holds) lines.push("Marks this session held.");
    if (next.publication === "publish") lines.push(next.review
      ? "Publishes the session plan, after you review its changes."
      : "Publishes the session plan.");
    else if (next.publicationWaiting) lines.push(PUBLICATION_WAITS);
    else lines.push(next.update ? "Publishes nothing." : state.planChanged
      ? "Publishes nothing new. Changes stay private, and the current publication stays as it is."
      : "Publishes nothing new.");
    const closes = next.holds || state.recovery && !state.recovery.update && !state.recovery.wasHeld && ["submit", "keep", "adopt"].includes(next.roll);
    if (closes && state.revealOnHold && next.publication !== "publish") lines.push(state.held
      ? "The previously published recap and next steps are already visible to students."
      : "The previously published recap and next steps become visible once the session is held.");
    return lines;
  };
  if (!state.canRecord) return { lines: [] };
  const recovery = state.recovery;
  if (recovery && !recovery.loaded) {
    const footer = act({ label: recovery.update ? "Update roll" : "Continue Close", roll: "choose", update: recovery.update });
    const theirs = act({ label: "Load their version", roll: "load", update: recovery.update });
    const mine = state.changedMarks ? act({ label: recovery.update ? "Keep my edits and update roll" : recovery.publish && state.planReady ? "Keep my edits, close and publish" : "Keep my edits and close", roll: "keep", publication: recovery.publish && !recovery.update ? "publish" : "none", update: recovery.update }) : undefined;
    return { footer, choices: { mine, theirs }, effectsLabel: (mine ? footer : theirs).label, lines: effects(mine ? footer : theirs) };
  }
  let footer: CloseAct | undefined;
  if (recovery) {
    footer = act({ label: "Continue Close", roll: state.changedMarks || !state.held ? "submit" : "adopt", holds: !state.held, publication: recovery.publish && !recovery.update ? "publish" : "none", update: recovery.update, keepsUnmarked: state.changedMarks });
  } else if (state.held) {
    if (state.changedMarks) footer = act({ label: "Update roll", roll: "submit", update: true });
    else if (state.checkPending) footer = act({ label: "Check again", publication: "check" });
    else if (state.confirmation === "private") footer = act({ label: "Keep changes private" });
    else if (state.planChanged || state.publicationFailed) footer = act({ label: state.prepareChanged ? "Review the session plan" : state.publicationFailed ? "Retry 1 item" : "Publish recap", publication: "publish", review: (!state.publicationFailed || state.prepareChanged) && !state.confirmation });
  } else if (state.statusPending || state.marked) {
    const publish = state.confirmation ? state.confirmation === "publish" : state.publish;
    const review = publish && state.planReady && state.prepareChanged && !state.confirmation;
    footer = act({ label: review ? "Review and close" : "Close session", roll: "submit", holds: true, publication: publish ? "publish" : "none", review });
  }
  return { footer, lines: footer ? effects(footer) : [] };
}

export interface CloseActOperations {
  submit: () => Promise<RollSubmitOutcome>;
  adopt: () => Promise<RollSubmitOutcome>;
  keep: () => Promise<RollSubmitOutcome>;
  load: () => Promise<RollSubmitOutcome>;
  publish: () => Promise<boolean>;
  check: () => Promise<void>;
  review: () => void;
}

/** The component and the branch table execute the same plan, including the write boundary. */
export async function executeCloseAct(act: CloseAct | undefined, operations: CloseActOperations): Promise<{ roll?: RollSubmitOutcome; published?: boolean }> {
  if (!act || act.roll === "choose") return {};
  if (act.review) { operations.review(); return {}; }
  if (act.publication === "check") { await operations.check(); return {}; }
  const roll = act.roll === "none" ? undefined : await operations[act.roll]();
  const confirmed = !roll || ((roll.kind === "recorded" || roll.kind === "adopted") && roll.roll.status === "held");
  const published = act.publication === "publish" && confirmed && act.roll !== "load" ? await operations.publish() : undefined;
  return { roll, published };
}
